<script lang="ts">
  // The Review view: the verdict, the summary, every draft with a box
  // to tick, and what publishing hands off. The decision is made here, at
  // the moment of sending: nothing is ticked to begin with, the button
  // sends exactly what is ticked, and each draft is managed in place
  // (components/DraftTriage: tick, edit, discard, open in Files). The
  // viewer's private notes on the pull request's files are listed apart
  // (components/ReviewNotes), each promoted to a draft by choice. The list
  // and the publish read one plan (`PrStore.planFor`, see logic/inbox
  // `publishPlan`), so what is ticked is what is sent, and the rest stay
  // drafts. Opening the page reads the viewer's pending review on GitHub
  // into the list and writes nothing to GitHub. The hand-off (logic/handoff
  // `handOffPlan`) and the reviewers asked again are applied once the
  // review is in (`PrStore.publishWithHandOff`), exactly as shown; how
  // that goes is the store's `aftermath` (logic/aftermath).
  import { onDestroy, onMount, tick, untrack } from 'svelte';
  import { availableVerdict, subject, type PrStore, type ReviewEvent } from '../lib/state/pr.svelte';
  import type { ReviewDraft } from '../lib/api/server';
  import { loadDraft, mergeText, saveDraft } from '../lib/state/drafts';
  import { adopt, fateOf, nothingToSend, planSummary, previewState, publishLabel, publishLead, repeatsApproval, sentCount } from '../lib/logic/inbox';
  import { blocksPublish, type StepState } from '../lib/logic/aftermath';
  import { carryOut, keyboard, useKeys } from '../lib/keys/keyboard.svelte';
  import { updateSend } from '../lib/commands/page';
  import type { Command } from '../lib/commands/command';
  import { controlCommand } from '../lib/keys/control';
  import { tabListKeys } from '../lib/keys/controls';
  import { applyToMembers, sameLogin } from '../lib/logic/attention';
  import { handOffPlan, reviewersOf, NO_EDITS, type HandOffEdits, type ReviewerState } from '../lib/logic/handoff';
  import { session } from '../lib/state/session.svelte';
  import { installLinkText } from '../lib/api/errors';
  import ErrorNotice from '../lib/components/ErrorNotice.svelte';
  import DraftTriage, { draftHref } from '../lib/components/DraftTriage.svelte';
  import ReviewNotes from '../lib/components/ReviewNotes.svelte';
  import type { DiffModel } from '../lib/state/diffs.svelte';
  import ReviewConversation from '../lib/components/ReviewConversation.svelte';
  import Html from '../lib/components/Html.svelte';

  interface Props {
    pr: PrStore;
    /** The pull request's own comparison, base → head, for the notes it shows (ReviewNotes); null until there is one. */
    diff?: DiffModel | null;
  }
  let { pr, diff = null }: Props = $props();

  let body = $state('');
  /** The verdict: Comment is none, just the comments (a review is a comment whatever its verdict). */
  let event: ReviewEvent = $state('COMMENT');
  /** A publish is in flight. */
  const publishing = $derived(!!pr.writes.on(subject.publish));
  /** Publish was pressed: set in the press's own frame, before the write is registered, so the page answers at once. */
  let sending = $state(false);
  /** A publish is on its way: the button, the drafts it sends and the note beside the button all say so. */
  const busy = $derived(sending || publishing);
  /** A publish or its hand-off is being sent: another one waits (see `blocksPublish`). */
  const blocked = $derived(busy || blocksPublish(pr.aftermath));
  /** How the last publish from this page went, said beside the button until the page is changed again. */
  let outcome: string | null = $state(null);
  /** The publish that failed and can be sent again as it was. */
  const failed = $derived(pr.writes.failures?.find((f) => f.kind === 'publish') ?? null);
  /** The viewer's pending review on GitHub is being read into the drafts: the list is not complete yet. */
  const reading = $derived(pr.writes.pending.some((w) => w.kind === 'mirror'));
  const readFailed = $derived(pr.writes.failures?.find((f) => f.kind === 'mirror') ?? null);
  const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
  const draftKey = $derived(`review:${pr.owner}/${pr.repo}#${pr.number}`);
  const open = $derived(pr.pr?.state === 'OPEN');
  const me = $derived(session.user?.login ?? pr.viewer);

  /**
   * The drafts as the page lists them, frozen when it opens (after the
   * viewer's pending review on GitHub is read in) and after each publish,
   * so a draft ticked or edited elsewhere meanwhile cannot slip into the
   * publish unseen.
   */
  let frozen: ReviewDraft[] | null = $state(null);
  /**
   * The first read, before the list is first frozen: the list is not complete, so it says so and nothing is published.
   * A later read (the pending review changed on GitHub, the tab came back to the front) runs in the background:
   * it shows nothing and gates nothing, so a click that brings the tab back acts. If it changes the drafts,
   * that is told as any change made elsewhere is (`changed`); the server reads the pending review itself in every publish.
   */
  const firstRead = $derived(reading && frozen === null);
  /** Drafts being changed on this page (ticked, edited, discarded, restored), once per write still being sent. */
  let changing: ReviewDraft['id'][] = $state([]);
  /** The list: the frozen drafts, with those being changed here as they are now (the viewer sees their own changes). */
  /** Until the list is first frozen (the pending review on GitHub is still being read), it is the drafts as they are: none is hidden by a slow read, and none can be published yet. */
  const listed = $derived<ReviewDraft[]>(frozen ? adopt(frozen, pr.drafts, new Set(changing)) : pr.drafts);
  /** The drafts changed elsewhere since the list was frozen: it must be looked at again before publishing. */
  const changed = $derived(!busy && frozen !== null && previewState(listed, pr.drafts) === 'changed');
  function freeze() {
    frozen = [...pr.drafts];
  }
  /** A change made on this page to draft `id`: it is followed while its write is sent, and kept once that is in (or rolled back). */
  function change(id: ReviewDraft['id'], write: Promise<unknown>) {
    outcome = null;
    changing = [...changing, id];
    void write.finally(() => {
      if (frozen) frozen = adopt(frozen, pr.drafts, new Set([id]));
      const at = changing.indexOf(id);
      changing = changing.filter((_, i) => i !== at);
    });
  }

  /** The drafts for the review, and those set aside (review/asides.ts), listed apart, folded: no publish sends one. */
  const forReview = $derived(listed.filter((d) => !pr.isAside(d.id)));
  const asides = $derived(listed.filter((d) => pr.isAside(d.id)));

  /** What publishing now publishes. */
  const plan = $derived(pr.planFor(event, body, forReview));
  /** The ticked comments on the whole pull request: sent in the review's body (logic/inbox `reviewBody`). */
  const prComments = $derived(plan.inline.filter((d) => d.target.kind === 'pr').length);
  /** How many comments it sends, of how many drafts. */
  const sent = $derived(sentCount(plan));
  const total = $derived(forReview.length);

  // The review lands on an older commit than the head (its line drafts were
  // made there, or the diff on screen predates a push).
  const staleCommit = $derived(pr.pr && plan.verdict && plan.commit && plan.commit !== pr.pr.headRefOid ? plan.commit : null);

  const canRequestChanges = $derived(pr.actionableCount > 0 && !pr.isAuthor);
  const canApprove = $derived(!pr.isAuthor);

  let verdicts: HTMLElement | undefined = $state();
  onMount(() => {
    // The summary of a publish being sent, or offered for Retry, is not offered again.
    void loadDraft(draftKey, { outbox: !pr.writes.holds(draftKey) }).then((b) => (body = mergeText(body, b)));
    // The viewer's pending review on GitHub is read into the drafts first, so the list is all of it. Only a read: GitHub is not changed.
    void (pr.mirrorsGitHub && !pr.draftsOff ? pr.mirrorDrafts() : Promise.resolve(true)).then(freeze);
    // Back in this tab: the pending review may have been changed, or submitted, on github.com meanwhile.
    window.addEventListener('focus', readOnFocus);
    unlisten = () => window.removeEventListener('focus', readOnFocus);
    // A failed summary given up comes back, beside what was typed since.
    return pr.writes.listen(draftKey, (t) => (body = mergeText(body, t)));
  });
  let unlisten = () => {};
  /** Read the viewer's pending review on GitHub again (a read only), unless a publish holds it. */
  function readPending() {
    if (pr.mirrorsGitHub && !pr.draftsOff && !busy) void pr.mirrorDrafts();
  }
  /** A read the tab's focus starts is one request to GitHub: at most one per this long, and none while one is under way. */
  const FOCUS_READ_EVERY = 30_000;
  let focusReadAt = -Infinity;
  function readOnFocus() {
    const now = Date.now();
    if (reading || now - focusReadAt < FOCUS_READ_EVERY) return;
    focusReadAt = now;
    readPending();
  }
  // The pull request's own read shows another pending review than the one last read (comments added
  // or removed on GitHub, the review submitted or deleted): the drafts follow it, so none outlives its comment unseen.
  let readFor: string | null = null;
  $effect(() => {
    const now = pr.pendingSignature;
    if (readFor !== null && now !== readFor) untrack(readPending);
    readFor = now;
  });
  // Leaving forgets the last publish's error and finished hand-off (see `PrStore.leftPublish`).
  onDestroy(() => {
    unlisten();
    pr.leftPublish();
  });

  // The page opens on the verdict: its checked choice takes focus once it is shown.
  let focused = false;
  $effect(() => {
    if (focused || !verdicts) return;
    focused = true;
    untrack(() => (verdicts?.querySelector<HTMLInputElement>('input:checked:not(:disabled)') ?? verdicts)?.focus());
  });

  // A verdict that stops being available (e.g. the last unresolved thread
  // went away) must not stay selected behind a disabled radio.
  $effect(() => {
    const next = availableVerdict(event, canApprove, canRequestChanges);
    if (next !== event) {
      if (event === 'REQUEST_CHANGES') dropped = 'Request changes is no longer available (no unresolved comment or draft left), so the verdict is now None.';
      event = next;
    }
  });
  /** Said when the selected verdict went away on its own; cleared once a verdict is picked. */
  let dropped = $state('');
  $effect(() => {
    if (event !== 'COMMENT') dropped = '';
  });

  /** Writes fail on a read-only repository: the page says so and its write controls are off. */
  const readOnlyTitle = $derived(pr.draftsOffText ?? undefined);

  // ------------------------------------------------------------ hand-off

  /** The viewer's changes to the default hand-off (which is on). */
  let edits: HandOffEdits = $state(NO_EDITS);
  const handOff = $derived(
    handOffPlan({ viewer: me, author: pr.author, members: pr.attention, canSet: (l) => pr.canSetAttention(l) }, edits),
  );
  /** Who will be waiting once the hand-off applies. */
  const waitingAfter = $derived(applyToMembers(pr.attention, handOff.changes));
  /** People one may add by hand: participants not waiting and not in the hand-off already, whose attention the viewer may set. */
  const addable = $derived(
    pr.participants.filter(
      (p) =>
        !pr.attention.some((m) => sameLogin(m.login, p.login)) &&
        !handOff.items.some((i) => sameLogin(i.login, p.login)) &&
        !sameLogin(p.login, me) &&
        pr.canSetAttention(p.login),
    ),
  );
  let adding = $state('');
  function toggle(login: string, on: boolean) {
    const off = edits.off.filter((o) => !sameLogin(o, login));
    edits = { ...edits, off: on ? off : [...off, login] };
  }
  /** Add the person picked (the Add button: arrowing through the list adds no one). */
  function addOther() {
    if (adding) edits = { ...edits, extra: [...edits.extra, adding] };
    adding = '';
  }
  function dropOther(login: string) {
    edits = { ...edits, extra: edits.extra.filter((x) => !sameLogin(x, login)) };
  }

  // ------------------------------------------------------------ reviewers

  const reviewers = $derived(pr.pr ? reviewersOf(pr.pr, me, pr.author) : []);
  /** Reviewers to ask again, alongside the publish: nobody until picked. */
  let reRequest: string[] = $state([]);
  const STATE_TEXT: Record<ReviewerState, string> = {
    APPROVED: 'approved',
    CHANGES_REQUESTED: 'requested changes',
    COMMENTED: 'commented',
    DISMISSED: 'review dismissed',
    REQUESTED: 'review requested',
  };
  function pickReviewer(login: string, on: boolean) {
    reRequest = on ? [...reRequest.filter((x) => x !== login), login] : reRequest.filter((x) => x !== login);
  }

  // ------------------------------------------------------------ publish

  /**
   * Why publishing waits on the rest of the pull request (the page opened on
   * its first answer): the verdicts go by every unresolved thread and the
   * hand-off by the whole timeline, so neither is offered on part of them.
   */
  const waiting = $derived(pr.partial ? 'Waiting for the rest of the pull request: the verdicts and the hand-off go by every thread and the whole timeline.' : null);

  /** Publishing would be allowed but for an Approve repeating the last one that went in, with nothing new. */
  const canSubmitElse = $derived.by(() => {
    // A tick or an edit still being sent is not what the server has yet.
    if (waiting || blocked || !open || pr.draftsOff || changing.length || frozen === null || previewState(listed, pr.drafts) !== 'current') return false;
    const ev: ReviewEvent = event;
    if (availableVerdict(ev, canApprove, canRequestChanges) !== ev) return false;
    // GitHub refuses a Comment review with nothing in it.
    if (plan.verdict === 'COMMENT' && !plan.body.trim() && !plan.inline.length) return false;
    return !!plan.verdict || plan.conversation.length > 0;
  });
  /** Why nothing would be published (nothing ticked, no summary and no verdict), in words; null when something would. */
  const nothing = $derived(nothingToSend(plan, event));
  /** The only reason the button is off is a repeat Approve (said beside it). */
  const repeat = $derived(canSubmitElse && repeatsApproval(pr.aftermath.sent, plan));
  const canSubmit = $derived(canSubmitElse && !repeat);

  /**
   * Publish the plan on screen: the drafts show as published at once and
   * the summary empties; the header says it is publishing. When the review
   * fails it is all rolled back and the toast offers Retry, which owns the
   * summary meanwhile (see Writes.holds); when only conversation comments
   * fail, they are kept and the next publish posts them. The hand-off and
   * the reviewers picked are applied once the review is in. As soon as it
   * is, the page starts over (Comment, no summary, the plan frozen again),
   * so another Approve is a deliberate choice.
   */
  async function publish() {
    if (!canSubmit) return;
    // Said here, in the press's own frame: the button, the drafts going and the note beside it are busy at once.
    sending = true;
    outcome = null;
    const going = plan;
    const after = { attention: handOff.changes, reviewers: reRequest.filter((r) => reviewers.some((x) => !x.team && x.reRequest && x.name === r)) };
    try {
      const sent = pr.publishWithHandOff(going, { key: draftKey, text: body }, after);
      body = '';
      tab = 'write';
      const res = await sent;
      if (res.reviewed || res.ok) {
        event = 'COMMENT';
        edits = NO_EDITS;
        reRequest = [];
        outcome = res.heldFailed
          ? `Review published. ${plural(res.heldFailed, 'conversation comment')} could not be posted and ${res.heldFailed === 1 ? 'is' : 'are'} kept.`
          : `Published: ${planSummary(going).replace(/^(\d+) published/, (_, n) => plural(Number(n), 'comment') + ' sent')}.`;
      }
    } finally {
      sending = false;
      freeze();
    }
  }

  /** The drafts the publish under way sends: each says so where it is listed. */
  const goingNow = $derived(busy ? new Set([...plan.inline, ...plan.conversation].map((d) => d.id)) : null);

  /** Send the failed publish again, as it was. */
  async function retry() {
    if (!failed || busy) return;
    sending = true;
    try {
      if (await failed.retry()) outcome = 'Published.';
    } finally {
      sending = false;
      freeze();
    }
  }

  // The outcome is said until the verdict or the summary is touched again.
  $effect(() => {
    if (event !== 'COMMENT' || body) outcome = null;
  });

  async function discard() {
    // The one thing here that reaches GitHub without publishing, so it is said before it is done.
    const github = pr.pendingReviewId ? '\n\nThis also deletes your pending review on GitHub, with every comment in it.' : '';
    const aside = asides.length ? ` (and ${plural(asides.length, 'draft')} set aside)` : '';
    if (!confirm(`Discard all ${plural(pr.draftCount, 'draft')}${aside}?${github}`)) return;
    const gone = pr.discardAllDrafts();
    // Every draft listed goes, by the viewer's own hand.
    for (const d of listed) change(d.id, gone);
    await gone;
  }

  // Sending publishes, from the summary too; the drafts' commands are the list's (DraftTriage); the rest are the page's.
  useKeys({ scope: 'send', handle: (command) => carryOut(updateSend(null, command)[1], () => void publish()) });
  const keyOf = (command: Command) => keyboard.key(command);

  // ------------------------------------------------------------ summary tabs

  const TABS = ['write', 'preview'] as const;
  let tab: (typeof TABS)[number] = $state('write');
  /** Arrow keys, Home and End switch between Write and Preview (see `tabListKeys`). */
  async function tabKeydown(e: KeyboardEvent) {
    const c = controlCommand(tabListKeys, e);
    if (!c) return;
    e.preventDefault();
    tab = c === 'first' ? 'write' : c === 'last' ? 'preview' : tab === 'write' ? 'preview' : 'write';
    await tick();
    document.getElementById(`summary-tab-${tab}`)?.focus();
  }

  /** The last publish's hand-off writes, as the store recorded them. */
  const steps = $derived(pr.aftermath.run.phase === 'landed' ? pr.aftermath.run.steps : []);
  /** One hand-off write in words: done (past) or not (to do). */
  function said(s: StepState, done: boolean): string {
    const st = s.step;
    if (st.kind === 'review') return `${done ? 'asked' : 'ask'} ${st.login} to review again`;
    const who = sameLogin(st.change.login, me) ? 'you' : st.change.login;
    return `${st.change.state === 'remove' ? (done ? 'removed' : 'remove') : done ? 'added' : 'add'} ${who}`;
  }
  /** How the hand-off goes, in words (the page's live regions): what is done, what is being sent, what failed and why. */
  const handOffText = $derived.by(() => {
    const by = (at: StepState['status']['at']) => steps.filter((s) => s.status.at === at);
    const parts: string[] = [];
    if (by('done').length) parts.push(`Handed off: ${by('done').map((s) => said(s, true)).join(', ')}.`);
    if (by('sending').length) parts.push(`Handing off: ${by('sending').map((s) => said(s, false)).join(', ')}…`);
    return parts.join(' ');
  });
  /** What did not go in, and why: failures first, then the changes held behind a failed review request. */
  const failedText = $derived(
    [
      ...steps.flatMap((s) => (s.status.at === 'failed' ? [`${said(s, false)} (${s.status.error})`] : [])),
      ...steps.flatMap((s) => (s.status.at === 'waiting' ? [`${said(s, false)} (waits for the review request)`] : [])),
    ].join('; '),
  );
</script>

<section class="review-view" aria-labelledby="review-title">
  <h2 id="review-title">Review</h2>

  {#if !open}
    <p class="page-note" role="note">Reviews can only be published on open pull requests.</p>
  {/if}
  {#if pr.readOnly}
    <p class="page-note" role="note">
      <ErrorNotice text={pr.readOnlyText ?? ''} link={pr.installLink} linkText={installLinkText(pr.owner)} />
    </p>
  {:else if !pr.serverData}
    <p class="page-note" role="note"><ErrorNotice text={pr.serverDataText ?? ''} /></p>
  {/if}
  {#if pr.pendingNote}
    <p class="page-error" role="alert"><ErrorNotice text={pr.pendingNote.text} /></p>
  {/if}
  {#if pr.heldNote}
    <p class="page-error" role="alert"><ErrorNotice text={pr.heldNote.text} link={pr.heldNote.link} linkText={pr.heldNote.linkText} /></p>
  {:else if pr.publishFailure && !pr.draftsOff}
    <p class="page-error" role="alert">
      <ErrorNotice text="Publishing failed: {pr.publishFailure.text}" link={pr.publishFailure.link} linkText={pr.publishFailure.linkText} />
    </p>
  {/if}
  {#if staleCommit && pr.pr}
    <p class="page-note stale-commit" role="note">
      This review is on commit <code>{staleCommit.slice(0, 7)}</code>, not the latest commit
      <code>{pr.pr.headRefOid.slice(0, 7)}</code>: new commits were pushed since its line comments were written.
    </p>
  {/if}


  <!-- 1. Unsent: every draft, with a box to tick; nothing is ticked to begin with. -->
  <section class="review-part unsent" class:stale={changed} aria-labelledby="unsent-title">
    <div class="part-head">
      <h3 id="unsent-title">Unsent <span class="tick-count" class:none={!sent}>{sent} of {total} ticked</span></h3>
      {#if total}
        <p class="keys-hint">
          <kbd>{keyOf({ kind: 'move', object: 'draft', dir: 1 })}</kbd>/<kbd>{keyOf({ kind: 'move', object: 'draft', dir: -1 })}</kbd> move ·
          {#each keyboard.keys({ kind: 'act', verb: 'include', advance: true }) as k, i}{#if i}{' or '}{/if}<kbd>{k}</kbd>{/each} tick ·
          <kbd>{keyOf({ kind: 'act', verb: 'edit' })}</kbd> edit · <kbd>{keyOf({ kind: 'act', verb: 'discard' })}</kbd> discard ·
          <kbd>{keyOf({ kind: 'act', verb: 'open' })}</kbd> open in Files · <kbd>{keyOf({ kind: 'open', menu: 'guide' })}</kbd> all keys
        </p>
      {/if}
    </div>
    {#if firstRead}
      <p class="part-note" role="status">Reading your pending review on GitHub…</p>
    {:else if readFailed}
      <p class="page-note" role="alert">Your pending review on GitHub could not be read: {readFailed.error.text}</p>
    {/if}
    {#if pr.heldUnavailable}
      <p class="page-note" role="alert">Your drafts could not be loaded from the Changeling server.</p>
    {/if}
    {#if pr.githubPendingCount && !firstRead}
      <p class="part-note">{plural(pr.githubPendingCount, 'comment')} in your pending review on GitHub {pr.githubPendingCount === 1 ? 'is' : 'are'} not listed here yet.</p>
    {/if}
    {#if total}
      <p class="part-note" id="tick-rule">Tick the comments to send. The rest stay here, unsent, for a later review.</p>
      <DraftTriage
        {pr}
        drafts={forReview}
        fate={(id) => fateOf(plan, id)}
        locked={busy ? 'Publishing: wait until it is done.' : changed ? 'Your drafts changed elsewhere. Choose Review again first.' : null}
        sending={goingNow}
        onchange={change}
      />
    {:else if frozen && !asides.length}
      <div class="drafts-empty card">
        <p>Nothing unsent. Comments you write in Files, and replies you write below, are kept here until you publish your review.</p>
        <p class="muted">
          Nothing starts out ticked: you tick what to send, whether you wrote it, an agent proposed it, or it comes from your pending review on GitHub.
        </p>
      </div>
    {/if}
  </section>

  {#if asides.length}
    <!-- Drafts set aside: the viewer's own (a conversation with their agent, say), never sent; listed folded, each to open or bring back. -->
    <details class="review-part asides">
      <summary><h3>Set aside <span class="tick-count none">{asides.length}</span></h3></summary>
      <p class="part-note">Kept for you and never sent, such as conversations with your agent. Bring one back to send it with a review.</p>
      <ul class="aside-list">
        {#each asides as d (d.id)}
          <li class="card aside-item">
            <a class="mono" href={draftHref(pr, d)}>{d.target.kind === 'pr' ? 'the pull request' : d.target.path}{'line' in d.target && d.target.line ? ` · line ${d.target.line}` : ''}</a>
            <span class="aside-snippet">{d.body.replace(/<!--[\s\S]*?-->/g, '').replace(/\s+/g, ' ').trim().slice(0, 140)}</span>
            <button class="micro" disabled={pr.serverDataText !== null} title={pr.serverDataText ?? 'Back to the drafts for your review (not included until you tick it)'} onclick={() => pr.setAside(d.id, false)}>Bring back</button>
          </li>
        {/each}
      </ul>
    </details>
  {/if}

  <!-- The viewer's private notes on this pull request's files, each promoted to a draft by choice (or why it can't be). -->
  <ReviewNotes {pr} {diff} />

  <!-- 2. Send: the verdict, the summary, the hand-off, and exactly what this publish sends. -->
  <section class="review-part send" aria-labelledby="send-title">
    <div class="part-head">
      <h3 id="send-title">Send</h3>
      <p class="publish-lead" aria-live="polite">{frozen ? publishLead(plan) : ''}</p>
    </div>
    <div class="publish-grid">
      <div class="publish-main">
      <div class="verdict-label" id="verdict-label">Verdict</div>
        <p class="page-note verdict-dropped" role="status" aria-live="polite">{dropped}</p>
        <div class="choice" role="radiogroup" aria-labelledby="verdict-label" title={readOnlyTitle} tabindex="-1" bind:this={verdicts}>
          <label class:disabled={pr.draftsOff}>
            <input type="radio" name="verdict" value="COMMENT" bind:group={event} disabled={pr.draftsOff} />
            <span><b>None</b><small>Just the comments.</small></span>
          </label>
          <label class:disabled={!canApprove || pr.draftsOff}>
            <input type="radio" name="verdict" value="APPROVE" bind:group={event} disabled={!canApprove || pr.draftsOff} />
            <span><b>Approve</b><small>{canApprove ? 'Looks good to me. The author can merge.' : 'Authors cannot approve their own pull request.'}</small></span>
          </label>
          <label class:disabled={!canRequestChanges || pr.draftsOff}>
            <input type="radio" name="verdict" value="REQUEST_CHANGES" bind:group={event} disabled={!canRequestChanges || pr.draftsOff} />
            <span
              ><b>Request changes</b><small
                >{canRequestChanges
                  ? 'Block merging until the unresolved comments are addressed.'
                  : 'Needs at least one unresolved comment or draft to tie the request to.'}</small
              ></span
            >
          </label>
        </div>

        <div class="summary-box">
          <div class="summary-tabs" role="tablist" aria-label="Review summary" tabindex="-1" onkeydown={tabKeydown}>
            {#each TABS as t}
              <button
                id="summary-tab-{t}"
                role="tab"
                aria-selected={tab === t}
                aria-controls="summary-panel-{t}"
                tabindex={tab === t ? 0 : -1}
                onclick={() => (tab = t)}>{t === 'write' ? 'Write' : 'Preview'}</button
              >
            {/each}
          </div>
          <div id="summary-panel-write" role="tabpanel" aria-labelledby="summary-tab-write" hidden={tab !== 'write'}>
            <textarea
              aria-label="Review summary"
              placeholder="Summary (optional, Markdown{keyboard.keysIn('send', { kind: 'submit' }) ? `, ${keyboard.keysIn('send', { kind: 'submit' })} to publish` : ''})"
              disabled={pr.draftsOff}
              title={readOnlyTitle}
              bind:value={body}
              oninput={() => saveDraft(draftKey, body)}
            ></textarea>
          </div>
          <div id="summary-panel-preview" class="summary-preview" role="tabpanel" aria-labelledby="summary-tab-preview" hidden={tab !== 'preview'} tabindex="0">
            <!-- The body as it is sent: the summary, then the ticked pull request comments. -->
            {#if plan.body.trim()}<Html md={plan.body} class="comment-body" />{:else}<p class="muted">Nothing to preview.</p>{/if}
          </div>
          {#if prComments}
            <p class="muted small summary-note">
              {prComments === 1 ? 'Your comment on the pull request is' : `Your ${prComments} comments on the pull request are`} added after the summary when you publish: Preview shows the body as it is sent.
            </p>
          {/if}
        </div>
      </div>
      <div class="publish-main">
            <fieldset class="publish-box handoff">
          <legend>After publishing: Waiting on</legend>
          {#if !pr.serverData}
            <p class="muted">The hand-off is off: {pr.serverDataText}</p>
          {:else}
            {#if handOff.items.length}
              <ul class="handoff-items">
                {#each handOff.items as item (item.login)}
                  <li>
                    {#if item.role === 'other'}
                      <span class="handoff-chip on">add <b>@{item.login}</b></span>
                      <button class="linkish" aria-label="Don't add {item.login}" disabled={pr.draftsOff || !open} onclick={() => dropOther(item.login)}>Remove</button>
                    {:else}
                      <label class="handoff-chip" class:on={item.on}>
                        <input type="checkbox" checked={item.on} disabled={pr.draftsOff || !open} onchange={(e) => toggle(item.login, e.currentTarget.checked)} />
                        {item.change} <b>@{item.login}</b>{item.role === 'you' ? ' (you)' : ' (author)'}
                      </label>
                    {/if}
                  </li>
                {/each}
              </ul>
            {:else}
              <p class="muted">Publishing changes nobody's attention.</p>
            {/if}
            {#each handOff.notes as note}<p class="muted">{note}</p>{/each}
            {#if addable.length}
              <div class="handoff-add">
                <label for="handoff-add">Also add</label>
                <select id="handoff-add" bind:value={adding} disabled={pr.draftsOff || !open}>
                  <option value="">Someone else…</option>
                  {#each addable as p (p.login)}<option value={p.login}>{p.login}{p.roles.length ? ` (${p.roles.join(', ')})` : ''}</option>{/each}
                </select>
                <button class="btn small" onclick={addOther} disabled={!adding || pr.draftsOff || !open}>Add</button>
              </div>
            {/if}
            <p class="handoff-after">
              Then waiting on: {#if waitingAfter.length}<b>{waitingAfter.map((m) => m.login).join(', ')}</b>{:else}nobody{/if}
            </p>
          {/if}
          {#if handOffText}
            <div class="handoff-outcome">{handOffText}</div>
          {/if}
          {#if failedText}
            <div class="handoff-outcome failed">
              <span>Not done: {failedText}.</span>
              <button class="btn small" onclick={() => void pr.retryHandOff()} disabled={blocked}>Retry</button>
            </div>
          {/if}
        </fieldset>

        <fieldset class="publish-box reviewers">
          <legend>Ask to review again</legend>
          {#if reviewers.length}
            <ul class="reviewer-list">
              {#each reviewers as r ((r.team ? 'team:' : 'user:') + r.name)}
                <li>
                  {#if r.reRequest}
                    <label>
                      <input
                        type="checkbox"
                        checked={reRequest.includes(r.name)}
                        disabled={pr.readOnly || !open}
                        onchange={(e) => pickReviewer(r.name, e.currentTarget.checked)}
                      />
                      <b>{r.name}</b>
                    </label>
                  {:else}
                    <span class="reviewer-static"><b>{r.name}</b>{#if r.team}<span class="muted"> (team)</span>{/if}</span>
                  {/if}
                  <span class="review-state {r.state.toLowerCase()}">{STATE_TEXT[r.state]}</span>
                </li>
              {/each}
            </ul>
          {:else}
            <p class="muted">Nobody else has reviewed or been asked to yet.</p>
          {/if}
        </fieldset>
        <div class="preview send-recap" aria-labelledby="recap-title">
          <div id="recap-title" class="eyebrow">What this sends</div>
          {#if plan.verdict}
            <div class="preview-review">
              <span class="verdict {plan.verdict.toLowerCase()}"
                >{plan.verdict === 'APPROVE' ? 'Approve' : plan.verdict === 'REQUEST_CHANGES' ? 'Request changes' : 'No verdict'}</span
              >
              {#if plan.body.trim()}<Html md={plan.body} class="comment-body preview-summary" />{:else}<span class="muted">No summary.</span>{/if}
            </div>
          {:else if plan.conversation.length}
            <p>No review is submitted (no verdict, no summary and nothing ticked for the review): only the conversation comments are posted.</p>
          {/if}
          <p>
            {#if sent}{plural(sent, 'ticked comment')}{:else}No comments{/if}{#if total - sent}; {plural(total - sent, 'draft')} {total - sent === 1 ? 'stays' : 'stay'} unsent{/if}.
          </p>
          {#if plan.resolve.length}
            <p>{plural(plan.resolve.length, 'thread')} you answered with Done or Ack will be resolved.</p>
          {/if}
        </div>
      </div>
    </div>
  </section>

  <!-- 3. Conversation: everything said in review so far, and the open threads to answer. -->
  <ReviewConversation {pr} />

  <div class="publish-actions">
    <button class="btn danger small" onclick={discard} disabled={!pr.draftCount || !!pr.busy || pr.draftsOff} title={readOnlyTitle} aria-label="Discard drafts"
      ><span class="wide-only">Discard drafts</span><span class="narrow-only">Discard</span></button
    >
    {#if busy}
      <span class="plan-summary publish-busy"><span class="spinner" aria-hidden="true"></span>Publishing… this page updates when GitHub has it.</span>
    {:else if failed}
      <!-- Where the button is: what went wrong, and the way to send the same publish again. -->
      <span class="plan-summary publish-failed" role="note"
        >Not published: {failed.error.text} <button class="btn small" onclick={retry}>Retry</button></span
      >
    {:else if outcome}
      <span class="plan-summary publish-done">{outcome}</span>
    {:else if waiting}
      <span class="plan-summary publish-why counting-note" id="publish-wait" role="note">{waiting}</span>
    {:else if repeat}
      <span class="plan-summary repeat-approval" role="note">You already approved. Add a summary or comments to approve again.</span>
    {:else if changed}
      <!-- Beside the disabled button: why it is disabled, and the way on. -->
      <span class="plan-summary preview-changed" role="alert"
        >Your drafts changed elsewhere — review again <button class="btn small" onclick={freeze}>Review again</button></span
      >
    {:else if nothing && frozen}
      <span class="plan-summary publish-why" id="publish-why" role="note">{nothing}</span>
    {:else}
      <span class="plan-summary" aria-live="polite">{frozen ? planSummary(plan) : ''}</span>
    {/if}
    <!-- While it is publishing the button stays readable (not :disabled, which dims it) and does nothing. -->
    <button
      class="btn publish-go {event === 'APPROVE' ? 'go' : 'primary'}"
      class:is-busy={busy}
      onclick={publish}
      disabled={!canSubmit && !busy}
      aria-disabled={busy ? 'true' : undefined}
      aria-busy={busy ? 'true' : undefined}
      title={readOnlyTitle}
      aria-describedby={waiting && !busy && !outcome && !failed ? 'publish-wait' : nothing && frozen && !repeat && !changed && !busy && !outcome && !failed ? 'publish-why' : undefined}
    >
      {#if busy}<span class="spinner" aria-hidden="true"></span>Publishing…{:else}{publishLabel(plan, event)}{/if}
    </button>
  </div>
</section>

<!-- The publish itself, announced politely: that it started, and how it went. -->
<div class="sr-only publish-said" role="status" aria-live="polite">{busy ? 'Publishing your review…' : (outcome ?? '')}</div>
<!-- How the hand-off goes, announced politely; a failure as an alert. -->
<div class="sr-only" role="status" aria-live="polite">{handOffText}</div>
<div class="sr-only" role="alert">{failedText ? `The hand-off did not finish: ${failedText}.` : ''}</div>

